import type { WorkflowCatalogNode, WorkflowNode } from './api'

/**
 * Compute the DAG depth (column) of each node for layout.
 * Mirrors backend/domain/workflow.ts computeDepths — kept in sync manually (pure functions).
 */
export function computeDepths(nodes: WorkflowNode[]): Map<string, number> {
  const depths = new Map<string, number>()

  function depth(nodeId: string, visiting = new Set<string>()): number {
    if (depths.has(nodeId)) return depths.get(nodeId)!
    const node = nodes.find((n) => n.nodeId === nodeId)
    if (!node || node.dependsOn.length === 0) {
      depths.set(nodeId, 0)
      return 0
    }
    visiting.add(nodeId)
    const d = Math.max(...node.dependsOn.map((dep) => (visiting.has(dep) ? 0 : depth(dep, visiting)))) + 1
    visiting.delete(nodeId)
    depths.set(nodeId, d)
    return d
  }

  for (const n of nodes) depth(n.nodeId)
  return depths
}

export function deriveProgress(nodes: WorkflowNode[]): {
  total: number
  valide: number
  pct: number
  allValide: boolean
  criticalPathBlocked: boolean
} {
  const enabled = nodes.filter((n) => n.enabled)
  const total = enabled.length
  const valide = enabled.filter((n) => n.statut === 'valide').length
  const pct = total === 0 ? 0 : Math.round((valide / total) * 100)
  const allValide = valide === total
  const criticalPathBlocked = enabled.filter((n) => n.surCheminCritique).some((n) => n.statut === 'bloque')
  return { total, valide, pct, allValide, criticalPathBlocked }
}

/**
 * Adapt a static catalog entry (`GET /v1/workflow/catalog`, no bid, no statut/dependsOn) into a
 * placeholder `WorkflowNode` — untouched ("à faire", no dependencies) since no work has happened
 * yet. Lets the chat's slash-menu reuse its normal WorkflowNode rendering before a workflow has
 * been created for the bid (see ChatPanel's `noWorkflowYet`).
 */
export function catalogNodeAsWorkflowNode(n: WorkflowCatalogNode, ordinal: number): WorkflowNode {
  return {
    nodeId: n.nodeId,
    section: n.section,
    statut: 'a_faire',
    dependsOn: [],
    surCheminCritique: false,
    enabled: n.enabled,
    ordinal,
    ...(n.skill ? { skill: n.skill } : {}),
    ...(n.navHidden ? { navHidden: true } : {}),
  }
}

/**
 * Which of the shipped passerelle skills the chat currently offers as a slash command.
 *
 * The workflow carries one skill per section (`client/passerelle/skills/niobe_*.md`), but for the
 * moment only these two are exposed in the chat; the others stay in the workflow everywhere else
 * (nav, dashboard, "generate" from the workflow view) and are simply not listed — nor routed — as
 * `/…` commands. Widen this set, or delete it together with `isSlashInvocable`, to bring them back.
 */
export const SLASH_COMMAND_SKILLS: ReadonlySet<string> = new Set(['niobe_qualify-rfx', 'niobe_references'])

/** True when a node's skill is one of the skills the chat offers as a slash command. */
export function isSlashInvocable(node: Pick<WorkflowNode, 'skill'>): boolean {
  return Boolean(node.skill && SLASH_COMMAND_SKILLS.has(node.skill))
}

export function statut_label(s: WorkflowNode['statut']): string {
  const map: Record<string, string> = {
    a_faire: 'À faire',
    en_cours: 'En cours',
    a_revoir: 'À revoir',
    valide: 'Validé',
    bloque: 'Bloqué',
  }
  return map[s] ?? s
}
